import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  Background, BackgroundVariant, Controls, MiniMap, Panel, MarkerType, Position, useNodesState, useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getFlowGraph, getAddressBalance, getHackIncidents, getFlowGraphHistory, getFlowGraphSnapshot } from '../api';
import HackCasePanel from '../components/HackCasePanel';

// NEAR intentionally omitted — the dedicated NearBlocks-backed tracer
// (NearFlowPage, still routed at /near-flow) is temporarily unlinked from
// navigation: NearBlocks' free tier (6 calls/min) can't reliably serve it
// alongside the Staking feature's own background polling on the same IP.
// Re-add here once that's resolved (paid NearBlocks key, or a queued/shared
// rate limiter across both features).
const CHAINS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'bnb', label: 'BNB Chain' },
  { value: 'solana', label: 'Solana' },
  { value: 'bitcoin', label: 'Bitcoin' },
];

const CIRCLE_SIZE = 40;
const RADIAL_STEP = 140;               // base ring-to-ring radius growth per hop
const MIN_ARC_GAP = CIRCLE_SIZE + 24;  // min px between adjacent nodes on the same ring
// A fan, not a closed circle: total angular span < 360°, centered so the gap
// sits at the back — matches the reference tool's multi-directional spread
// without wrapping edges around the seam.
const FAN_SPAN = (300 * Math.PI) / 180;
const FAN_START = (-150 * Math.PI) / 180;
const LAST_SEARCH_KEY = 'tf_flowgraph_last';
const ACTIVE_CASE_KEY = 'tf_flowgraph_case';

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function monthsAgoIso(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}

// "0xabc; 0xdef;;  0xghi" -> ['0xabc','0xdef','0xghi'] — trims, drops empty
// entries, and dedupes so tracking the same wallet twice by mistake doesn't
// double-count it as two seeds.
const shortAddr = (a) => (a && a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-4)}` : a || '');

function parseSeeds(input) {
  return [...new Set(String(input || '').split(';').map((s) => s.trim()).filter(Boolean))];
}

// Sunburst-style radial layout: seed at the center; each node sits inside
// the angular slice of whichever node introduced it, and that slice is then
// subdivided among ITS children — so a node's edges stay close to its real
// parent's direction instead of every hop splitting a ring independently of
// who's actually connected to whom (which caused edges to sweep across the
// whole canvas). Nodes with bigger downstream branches get a proportionally
// wider slice, so the fan opens wider where the trace goes deeper — this is
// what gives the multi-directional "hình quạt" spread instead of a single
// left-to-right column. Now much less crowded than the first attempt since
// meta-node grouping + the per-hop cap both run before this.
function layoutNodes(visibleNodes, visibleFlows) {
  const byAddr = new Map(visibleNodes.map((n) => [n.address, n]));
  const hopOf = (a) => byAddr.get(a)?.hop;
  // Multi-wallet tracking: every tracked address is its own hop-0 root — all
  // of them share the SAME polar origin (0,0), each getting its own angular
  // sector (like spokes of a flower) instead of one seed sitting dead center.
  const seedAddrs = visibleNodes.filter((n) => n.hop === 0).map((n) => n.address);
  if (seedAddrs.length === 0) return [];

  // One "layout parent" per non-seed node: the highest-value flow linking it
  // to a node exactly one hop closer to a seed. Direction (in/out) doesn't
  // matter here — only which two nodes are hop-adjacent.
  const parentOf = new Map();
  const bestAmount = new Map();
  for (const f of visibleFlows) {
    const hopA = hopOf(f.from);
    const hopB = hopOf(f.to);
    if (hopA == null || hopB == null) continue;
    let parent, child;
    if (hopA === hopB - 1) { parent = f.from; child = f.to; }
    else if (hopB === hopA - 1) { parent = f.to; child = f.from; }
    else continue;
    if (!bestAmount.has(child) || f.amountUsd > bestAmount.get(child)) {
      bestAmount.set(child, f.amountUsd);
      parentOf.set(child, parent);
    }
  }

  const children = new Map();
  for (const n of visibleNodes) {
    if (n.hop === 0) continue;
    // A node only reachable via a same-hop or backward link (rare) has no
    // valid hop-adjacent parent — attach it to the first seed instead of
    // dropping it.
    const p = parentOf.get(n.address) ?? seedAddrs[0];
    if (!children.has(p)) children.set(p, []);
    children.get(p).push(n.address);
  }

  const weight = new Map();
  function subtreeWeight(addr) {
    const kids = children.get(addr) || [];
    const w = kids.length === 0 ? 1 : kids.reduce((sum, k) => sum + subtreeWeight(k), 0);
    weight.set(addr, w);
    return w;
  }
  seedAddrs.forEach(subtreeWeight);

  const angleByAddr = new Map();
  function assignAngles(addr, start, end) {
    angleByAddr.set(addr, (start + end) / 2);
    const kids = children.get(addr) || [];
    if (kids.length === 0) return;
    const totalW = kids.reduce((sum, k) => sum + (weight.get(k) || 1), 0) || kids.length;
    let cursor = start;
    for (const k of kids) {
      const span = (end - start) * ((weight.get(k) || 1) / totalW);
      assignAngles(k, cursor, cursor + span);
      cursor += span;
    }
  }

  if (seedAddrs.length === 1) {
    // Single wallet: keep the original fan-with-a-gap convention.
    assignAngles(seedAddrs[0], FAN_START, FAN_START + FAN_SPAN);
  } else {
    // Multiple wallets: divide the FULL circle among them (no back-gap
    // needed once there's more than one hub), weighted by subtree size so a
    // wallet with a much bigger trace gets proportionally more room.
    const totalSeedWeight = seedAddrs.reduce((sum, a) => sum + (weight.get(a) || 1), 0);
    let cursor = -Math.PI;
    for (const seedAddr of seedAddrs) {
      const span = 2 * Math.PI * ((weight.get(seedAddr) || 1) / totalSeedWeight);
      assignAngles(seedAddr, cursor, cursor + span);
      cursor += span;
    }
  }

  // A single seed sits exactly at the shared origin (radius 0), same as
  // before. With multiple seeds, they sit on a small ring around the shared
  // origin instead of stacking on top of each other, and every node's real
  // hop still adds RADIAL_STEP on top of that.
  const SEED_RING_RADIUS = seedAddrs.length > 1 ? 70 : 0;

  // Radius per ring uses each node's REAL hop (not recursion depth, which a
  // fallback-attached node would get wrong), and grows past the base step
  // whenever that ring's tightest adjacent-angle gap would otherwise pack
  // nodes closer than MIN_ARC_GAP.
  const byHop = new Map();
  for (const n of visibleNodes) {
    if (!byHop.has(n.hop)) byHop.set(n.hop, []);
    byHop.get(n.hop).push(n);
  }

  const fullSweep = seedAddrs.length > 1 ? 2 * Math.PI : FAN_SPAN;
  const positioned = [];
  for (const [hop, group] of byHop) {
    const angles = group.map((n) => angleByAddr.get(n.address) ?? 0).sort((a, b) => a - b);
    let minGap = Infinity;
    for (let i = 0; i < angles.length - 1; i++) minGap = Math.min(minGap, angles[i + 1] - angles[i]);
    if (!Number.isFinite(minGap)) minGap = fullSweep;
    const baseRadius = SEED_RING_RADIUS + hop * RADIAL_STEP;
    const radius = Math.max(baseRadius, MIN_ARC_GAP / Math.max(minGap, 0.001));
    group.forEach((n) => {
      const angle = angleByAddr.get(n.address) ?? 0;
      positioned.push({ ...n, x: radius * Math.cos(angle), y: radius * Math.sin(angle), angle });
    });
  }
  return positioned;
}

// Which fixed handle side (Top/Right/Bottom/Left) faces toward vs away from
// the center, for a node sitting at the given angle on its ring. ReactFlow's
// default node type only has ONE source handle + ONE target handle per node,
// so every edge touching it launches/lands on the same point regardless of
// which neighbor it connects to — facing "outward" away from center and
// "inward" toward it keeps edges radiating cleanly instead of jogging
// sideways to reach a handle on the wrong side of the node.
function radialHandles(angle) {
  const deg = ((angle * 180) / Math.PI + 360) % 360;
  if (deg >= 315 || deg < 45) return { outward: Position.Right, inward: Position.Left };
  if (deg >= 45 && deg < 135) return { outward: Position.Bottom, inward: Position.Top };
  if (deg >= 135 && deg < 225) return { outward: Position.Left, inward: Position.Right };
  return { outward: Position.Top, inward: Position.Bottom };
}

function labelText(labels) {
  if (!labels || labels.length === 0) return null;
  return labels[0].replace(/\s*\[0x[0-9a-f]+\]$/i, '').trim();
}

// `glow` is a semi-transparent version of `border` for the node's box-shadow
// halo — kept as its own field (not derived inline) so the neon-glow look on
// the dark canvas has one definition per category, shared by the node
// circle, its minimap dot, and the legend swatches.
function nodeColor(node) {
  if (node.hop === 0) return { bg: '#7c3aed22', border: '#7c3aed', glow: '#7c3aed99' };
  const text = labelText(node.labels);
  if (!text) return { bg: 'var(--surface)', border: '#4b5563', glow: '#4b556377' };
  if (/binance|okx|coinbase|kraken|bybit|exchange|cex/i.test(text)) return { bg: '#f59e0b22', border: '#f59e0b', glow: '#f59e0b99' };
  if (/uniswap|dex|router|pool/i.test(text)) return { bg: '#0ea5e922', border: '#0ea5e9', glow: '#0ea5e999' };
  if (/tornado|mixer|sanction|scam|phish|hack|exploit/i.test(text)) return { bg: '#dc262622', border: '#dc2626', glow: '#dc262699' };
  return { bg: '#10b98122', border: '#10b981', glow: '#10b98199' };
}

// Per-tab cache of address -> total USD balance, so hovering the same node
// twice in one session never re-triggers the (1-credit) balance call — the
// backend caches too, but this skips the round-trip entirely on repeats.
const balanceCache = new Map();

// A wallet address rendered as a small glowing circle on the dark canvas
// (short address abbreviation only, or "×N" for a grouped meta-node).
// Clicking it opens the pinned detail panel (see PinnedTooltip) — no
// per-node hover tooltip anymore, since one anchored to the node scaled with
// canvas zoom and vanished on mouse-out before you could click-copy.
function AddressNode({ n }) {
  const [hover, setHover] = useState(false);
  const { border, glow } = nodeColor(n);
  const size = n.isMeta ? CIRCLE_SIZE + 8 : CIRCLE_SIZE;
  const label = n.hop === 0 ? 'SEED' : n.isMeta ? `×${n.memberCount}` : (n.address || '').replace(/^0x/i, '').slice(-4).toUpperCase();

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: size, height: size, borderRadius: '50%',
        background: `radial-gradient(circle at 35% 30%, ${border}33, #05070d 78%)`,
        border: `${n.isMeta ? 2.5 : 1.5}px solid ${border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: n.isMeta ? 9 : 8, fontWeight: 800, color: '#e8ecf5', fontFamily: 'monospace',
        cursor: 'pointer',
        boxShadow: hover
          ? `0 0 14px 3px ${glow}, 0 0 0 3px ${border}55`
          : `0 0 8px 1px ${glow}`,
        transition: 'box-shadow 0.15s',
      }}
    >
      {label}
    </div>
  );
}

// Detail panel for the currently-selected node, pinned to the canvas corner
// via ReactFlow's <Panel> — that renders OUTSIDE the pan/zoom-transformed
// layer (same layer as Controls/MiniMap), so it stays a fixed size and
// position regardless of how the graph is zoomed, unlike the old
// node-anchored hover tooltip. Only closes via the ✕ button or by selecting
// a different node — never on mouse-out — so there's time to click-copy.
const LEGEND_KEYS = [
  { color: '#7c3aed', key: 'flowGraph.legendSeed' },
  { color: '#f59e0b', key: 'flowGraph.legendCex' },
  { color: '#0ea5e9', key: 'flowGraph.legendDex' },
  { color: '#dc2626', key: 'flowGraph.legendRisk' },
  { color: '#10b981', key: 'flowGraph.legendOther' },
  { color: '#4b5563', key: 'flowGraph.legendUnlabeled' },
];

// Static legend pinned bottom-left, same untransformed layer as the detail
// panel — explains the node color coding + edge direction + the "×N"
// meta-node badge, since none of that is otherwise self-explanatory on a
// dark canvas full of colored circles.
function GraphLegend() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #0d1220, #080b14)', border: '1px solid #2a3352',
        borderRadius: 10, padding: '8px 10px', fontSize: 10, color: '#c3c9dc',
        boxShadow: '0 8px 28px rgba(0,0,0,0.5)', pointerEvents: 'none',
      }}
    >
      {LEGEND_KEYS.map((it) => (
        <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: it.color, boxShadow: `0 0 5px ${it.color}` }} />
          {t(it.key)}
        </div>
      ))}
      <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #22283e', color: '#8891ac' }}>
        {t('flowGraph.legendMetaNote')}
      </div>
    </div>
  );
}

function CopyableAddress({ address, copiedAddr, onCopy }) {
  const { t } = useTranslation();
  const copied = copiedAddr === address;
  return (
    <div
      onClick={() => onCopy(address)}
      title={t('flowGraph.clickToCopy')}
      style={{
        fontSize: 10, color: copied ? '#22d3a8' : '#9aa4bf', fontFamily: 'monospace',
        wordBreak: 'break-all', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
      }}
    >
      {copied ? t('flowGraph.copied') : address}
      {!copied && <span style={{ fontSize: 9, flexShrink: 0 }}>📋</span>}
    </div>
  );
}

function PinnedTooltip({ node, onClose }) {
  const { t } = useTranslation();
  const [balance, setBalance] = useState(null);
  const [copiedAddr, setCopiedAddr] = useState(null);

  useEffect(() => {
    setCopiedAddr(null);
    // Meta-nodes represent several addresses at once — no single balance to
    // fetch here; open each member individually (below) to check its own.
    if (!node || node.isMeta) { setBalance(null); return; }
    const key = `${node.chain}:${node.address}`;
    const cached = balanceCache.get(key);
    if (cached) { setBalance(cached); return; }
    let cancelled = false;
    setBalance('loading');
    balanceCache.set(key, 'loading');
    getAddressBalance(node.address, node.chain)
      .then((r) => { if (!cancelled) { balanceCache.set(key, r); setBalance(r); } })
      .catch(() => { if (!cancelled) { balanceCache.delete(key); setBalance('error'); } });
    return () => { cancelled = true; };
  }, [node]);

  if (!node) return null;
  const text = labelText(node.labels);

  const copyAddress = (addr) => {
    navigator.clipboard.writeText(addr).then(() => {
      setCopiedAddr(addr);
      setTimeout(() => setCopiedAddr(null), 1500);
    }).catch(() => {});
  };

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #0d1220, #080b14)', border: '1px solid #2a3352',
        borderRadius: 10, padding: '10px 12px', minWidth: 230, maxWidth: 270,
        boxShadow: '0 8px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(124,58,237,0.08)',
        textAlign: 'left', pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {node.hop === 0 && <div style={{ fontSize: 9, fontWeight: 800, color: '#a78bfa', marginBottom: 4 }}>SEED</div>}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#e8ecf5', marginBottom: 4 }}>
            {text || t('flowGraph.unlabeledAddress')}
            {node.isMeta && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#7dd3fc' }}>×{node.memberCount} {t('flowGraph.walletsSuffix')}</span>}
          </div>
        </div>
        <button
          onClick={onClose}
          title={t('flowGraph.close')}
          style={{ background: 'transparent', border: 'none', color: '#6b7492', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2, flexShrink: 0 }}
        >✕</button>
      </div>

      {node.isMeta ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 130, overflowY: 'auto', marginTop: 2 }}>
          {node.memberAddresses.map((addr) => (
            <CopyableAddress key={addr} address={addr} copiedAddr={copiedAddr} onCopy={copyAddress} />
          ))}
        </div>
      ) : (
        <CopyableAddress address={node.address} copiedAddr={copiedAddr} onCopy={copyAddress} />
      )}

      {node.chain && <div style={{ fontSize: 9, color: '#8891ac', marginTop: 4 }}>{t('flowGraph.network', { chain: node.chain })}</div>}
      {node.totalVolumeUsd > 0 && (
        <div style={{ fontSize: 9, color: '#8891ac', marginTop: 2 }}>{t('flowGraph.totalVolume', { amount: fmtUsd(node.totalVolumeUsd) })}</div>
      )}
      <div style={{ fontSize: 9, color: '#6b7492', marginTop: 2 }}>{t('flowGraph.hopLabel', { hop: node.hop })}</div>
      {node.isMeta && (
        <div style={{ fontSize: 9, color: '#6b7492', marginTop: 4, borderTop: '1px solid #22283e', paddingTop: 4 }}>
          {t('flowGraph.metaCopyHint')}
        </div>
      )}
      {balance === 'loading' && (
        <div style={{ fontSize: 9, color: '#8891ac', marginTop: 4 }}>{t('flowGraph.loadingBalance')}</div>
      )}
      {balance === 'error' && (
        <div style={{ fontSize: 9, color: '#f87171', marginTop: 4 }}>{t('flowGraph.balanceError')}</div>
      )}
      {balance && balance !== 'loading' && balance !== 'error' && (
        <div style={{ fontSize: 10, color: '#8891ac', marginTop: 4, borderTop: '1px solid #22283e', paddingTop: 4 }}>
          {t('flowGraph.totalAssets')} <strong style={{ color: '#e8ecf5' }}>{fmtUsd(balance.totalUsd)}</strong> {t('flowGraph.tokenCountSuffix', { count: balance.tokenCount })}
        </div>
      )}
    </div>
  );
}

// Only draw money-flow edges above this size — smaller transfers add visual
// noise without changing the investigative picture. Nodes left with no
// qualifying edge are dropped too (except the seed, always shown).
const MIN_EDGE_USD = 200000;
// Cap on how many nodes render per hop/ring, keeping the top ones by total
// volume — a real seed can have hundreds of hop2/hop3 counterparties, and no
// ring layout stays readable (or even keeps a sane radius) past a few dozen.
const MAX_NODES_PER_HOP = 24;

// Fixed, thin stroke — a flat 2px reads better than the previous log-scaled
// 1-4px range, which still let $B+ flows look like thick ribbons.
function edgeWidth() {
  return 2;
}

function buildFlowElements(graph, focusAddress, positionOverrides) {
  if (!graph) return { nodes: [], edges: [] };

  // Cap how many nodes render per hop, keeping the highest-volume ones —
  // independent of the $10k edge threshold below. A ring with 70-170+ nodes
  // is unreadable at any circle size, and forcing them all to fit blows the
  // ring radius up so far (to avoid overlap) that it dwarfed the inner
  // rings and made the whole graph look like a single ring at any zoom
  // level. This caps ring SIZE directly instead of fighting the symptom.
  const byHopRaw = new Map();
  for (const n of graph.nodes) {
    if (!byHopRaw.has(n.hop)) byHopRaw.set(n.hop, []);
    byHopRaw.get(n.hop).push(n);
  }
  const keptAddresses = new Set();
  for (const [hop, group] of byHopRaw) {
    const kept = hop === 0
      ? group
      : [...group].sort((a, b) => (b.totalVolumeUsd ?? 0) - (a.totalVolumeUsd ?? 0)).slice(0, MAX_NODES_PER_HOP);
    kept.forEach((n) => keptAddresses.add(n.address));
  }

  // Each backend edge carries BOTH directions on one row (Nansen's
  // counterparties response gives volume_in_usd/volume_out_usd together) —
  // split into up to two directed money flows so inbound transfers get their
  // own arrow instead of being silently dropped. `from` is always the actual
  // sender, `to` the actual receiver, regardless of which side of the
  // original hop-expansion call they were on.
  const flows = [];
  for (const e of graph.edges) {
    if (!keptAddresses.has(e.from) || !keptAddresses.has(e.to)) continue;
    if ((e.volumeOutUsd ?? 0) >= MIN_EDGE_USD) {
      flows.push({ from: e.from, to: e.to, amountUsd: e.volumeOutUsd });
    }
    if ((e.volumeInUsd ?? 0) >= MIN_EDGE_USD) {
      flows.push({ from: e.to, to: e.from, amountUsd: e.volumeInUsd });
    }
  }

  // Keep only the subgraph actually reachable from the seed through
  // qualifying flows. A node that only clears the threshold via some
  // downstream edge — with no path back to the seed — used to render
  // floating with no visible link; now it's dropped instead. This also
  // naturally drops anything that was only reachable through a node the
  // per-hop cap above excluded.
  const adjacency = new Map();
  const link = (a, b) => { if (!adjacency.has(a)) adjacency.set(a, new Set()); adjacency.get(a).add(b); };
  flows.forEach((f) => { link(f.from, f.to); link(f.to, f.from); });

  // Multi-wallet tracking: every tracked address is its own hop-0 seed, all
  // reachable/BFS-rooted together in the same pass.
  const seedNodes = graph.nodes.filter((n) => n.hop === 0);
  const seedAddrSet = new Set(seedNodes.map((n) => n.address));
  const reachable = new Set();
  const queue = [];
  for (const sn of seedNodes) { reachable.add(sn.address); queue.push(sn.address); }
  while (queue.length) {
    const cur = queue.shift();
    for (const next of adjacency.get(cur) || []) {
      if (!reachable.has(next)) { reachable.add(next); queue.push(next); }
    }
  }

  let visibleNodes = graph.nodes.filter((n) => keptAddresses.has(n.address) && reachable.has(n.address));
  let visibleFlows = flows.filter((f) => reachable.has(f.from) && reachable.has(f.to));

  // Group same-entity addresses into one "×N" meta-node. An entity like
  // "Binance Hot Wallet" often shows up as several separate addresses in the
  // trace; collapsing them by their resolved label keeps the graph from
  // repeating what is functionally the same counterparty N times. Only
  // labeled nodes are grouped — two "chưa gắn nhãn" addresses aren't
  // provably the same entity, so they stay separate.
  const byLabel = new Map();
  for (const n of visibleNodes) {
    if (n.hop === 0) continue;
    const text = labelText(n.labels);
    if (!text) continue;
    if (!byLabel.has(text)) byLabel.set(text, []);
    byLabel.get(text).push(n);
  }

  const addressToMetaId = new Map();
  const metaNodes = [];
  for (const [text, members] of byLabel) {
    if (members.length < 2) continue;
    const metaId = `meta:${text}`;
    members.forEach((m) => addressToMetaId.set(m.address, metaId));
    metaNodes.push({
      address: metaId, isMeta: true,
      memberAddresses: members.map((m) => m.address),
      memberCount: members.length,
      labels: members[0].labels, chain: members[0].chain,
      hop: Math.min(...members.map((m) => m.hop)),
      totalVolumeUsd: members.reduce((sum, m) => sum + (m.totalVolumeUsd || 0), 0),
    });
  }

  if (metaNodes.length > 0) {
    visibleNodes = [...visibleNodes.filter((n) => !addressToMetaId.has(n.address)), ...metaNodes];

    const redirect = (addr) => addressToMetaId.get(addr) || addr;
    const merged = new Map(); // "from|to" -> summed amountUsd
    for (const f of visibleFlows) {
      const from = redirect(f.from);
      const to = redirect(f.to);
      if (from === to) continue; // now-internal flow between grouped members
      const key = `${from}|${to}`;
      merged.set(key, (merged.get(key) || 0) + f.amountUsd);
    }
    visibleFlows = [...merged].map(([key, amountUsd]) => {
      const [from, to] = key.split('|');
      return { from, to, amountUsd };
    });
  }

  // Double-click focus: narrow the view down to just every tracked seed, the
  // double-clicked node (address OR meta id — grouping above already ran,
  // so a meta id here matches correctly), and whatever it's directly
  // connected to. Runs after grouping so a click on a meta-node focuses the
  // whole group, not one arbitrary member. All seeds stay visible (not just
  // the nearest one) so a multi-wallet trace keeps its full context.
  if (focusAddress && !seedAddrSet.has(focusAddress)) {
    const related = new Set([...seedAddrSet, focusAddress]);
    visibleFlows.forEach((f) => {
      if (f.from === focusAddress) related.add(f.to);
      if (f.to === focusAddress) related.add(f.from);
    });
    visibleNodes = visibleNodes.filter((n) => related.has(n.address));
    visibleFlows = visibleFlows.filter((f) => related.has(f.from) && related.has(f.to));
  }

  const positioned = layoutNodes(visibleNodes, visibleFlows);

  const nodes = positioned.map((n) => {
    const { outward, inward } = radialHandles(n.angle || 0);
    // A manual drag overrides the auto-computed radial position — kept
    // separately from layoutNodes so re-running the auto layout (new
    // search, hop toggle) never fights a position the user set by hand.
    const override = positionOverrides && positionOverrides[n.address];
    const x = override ? override.x : n.x;
    const y = override ? override.y : n.y;
    return {
      id: n.address,
      position: { x, y },
      // Each node's fixed source/target handle faces away from / toward the
      // seed based on its angle — with straight edges this keeps the line
      // from having to jog sideways to reach a handle on the wrong side.
      sourcePosition: outward,
      targetPosition: inward,
      // `raw` carries the full node object (including meta-node fields like
      // isMeta/memberAddresses) so click handlers can read it directly
      // instead of re-searching graph.nodes, which never has meta ids.
      data: { label: <AddressNode n={n} />, minimapColor: nodeColor(n).border, raw: n },
      // Let AddressNode own its own circle/background/border — strip
      // ReactFlow's default node chrome (white box, padding, border) so it
      // doesn't wrap the circle in a rectangle.
      style: { background: 'transparent', border: 'none', padding: 0, width: CIRCLE_SIZE, height: CIRCLE_SIZE },
    };
  });

  // Every node's outward handle faces away from the seed and its inward
  // handle faces toward it, so an edge always has to run inner-ring-node →
  // outer-ring-node to use those handles cleanly (reversing it would launch
  // from the outward side and have to loop back). Which end actually gets
  // the arrowhead — markerStart vs markerEnd — is what shows the TRUE money
  // direction, independent of that geometry. `type: 'straight'` draws a
  // literal straight line instead of a bezier curve, matching the reference
  // tool's look and reading cleaner on a multi-directional fan than curves do.
  const hopByAddress = new Map(visibleNodes.map((n) => [n.address, n.hop]));
  const edges = visibleFlows.map((f, i) => {
    const hopFrom = hopByAddress.get(f.from) ?? 0;
    const hopTo = hopByAddress.get(f.to) ?? 0;
    const geomInner = hopFrom <= hopTo ? f.from : f.to;
    const geomOuter = hopFrom <= hopTo ? f.to : f.from;
    const destinationIsInner = f.to === geomInner; // money flows back toward the seed side
    const width = edgeWidth(f.amountUsd);
    // Brighter neon tones + a matching glow (drop-shadow) so the lines read
    // clearly against the dark canvas instead of the flatter blue/green used
    // on the light theme.
    const color = destinationIsInner ? '#38bdf8' : '#34d399'; // cyan = inbound, green = outbound
    return {
      id: `e${i}-${f.from}-${f.to}`,
      source: geomInner,
      target: geomOuter,
      type: 'straight',
      label: fmtUsd(f.amountUsd),
      labelStyle: { fontSize: 9, fill: '#c3c9dc', fontWeight: 600 },
      labelBgStyle: { fill: '#0b0f1a', fillOpacity: 0.85 },
      style: { stroke: color, strokeWidth: width, filter: `drop-shadow(0 0 3px ${color}aa)` },
      markerStart: destinationIsInner ? { type: MarkerType.ArrowClosed, color } : undefined,
      markerEnd: destinationIsInner ? undefined : { type: MarkerType.ArrowClosed, color },
      animated: false,
    };
  });

  // Per-hop node counts, both what's actually rendered (after the $10k edge
  // threshold + reachability pruning) and what the backend returned before
  // any of that filtering — surfaced in the UI so "why is there only one
  // ring" is answerable by looking at the numbers instead of guessing.
  const visibleHopCounts = {};
  visibleNodes.forEach((n) => { visibleHopCounts[n.hop] = (visibleHopCounts[n.hop] || 0) + 1; });
  const rawHopCounts = {};
  graph.nodes.forEach((n) => { rawHopCounts[n.hop] = (rawHopCounts[n.hop] || 0) + 1; });

  return { nodes, edges, visibleHopCounts, rawHopCounts };
}

export default function FlowGraphPage() {
  const { t } = useTranslation();
  // Raw text field — one or more addresses separated by ";" (parseSeeds
  // handles trimming/dedup/empty-entry cleanup at submit time).
  const [seedInput, setSeedInput] = useState('');
  const [chain, setChain] = useState('ethereum');
  const [depth, setDepth] = useState(2);
  const [from, setFrom] = useState(monthsAgoIso(1));
  const [to, setTo] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [graph, setGraph] = useState(null);
  const [hopStats, setHopStats] = useState(null);
  // Double-click focus: narrows the canvas to every seed + one clicked node
  // + its direct connections. null = show everything (normal view).
  const [focusAddress, setFocusAddress] = useState(null);
  // Node currently shown in the pinned top-left detail panel (raw backend
  // node object) — null = panel hidden.
  const [activeNode, setActiveNode] = useState(null);

  // Manual drag overrides for the CURRENT graph — address -> {x,y}. Kept
  // separate from the auto-computed radial layout so re-running it (new
  // search) doesn't fight a position the user set by hand, but a drag on the
  // current graph sticks across focus/zoom changes and survives F5 (see the
  // localStorage wiring below).
  const [positions, setPositions] = useState({});

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // A link with ?seeds=a;b&chain=&depth=&from=&to= (e.g. from Hack Tracker)
  // fills the form and runs that trace — cache-first on the backend, so a
  // trace someone already ran costs no new credits. Otherwise restore the
  // last search on mount, so returning to the page shows the previous trace
  // instead of an empty canvas (0 credit — no API call).
  const [searchParams] = useSearchParams();

  // Quick picks: the pre-built traces of every incident in Hack Tracker, so
  // a case can be opened here directly instead of retyping its wallets.
  const { i18n } = useTranslation();
  const [cases, setCases] = useState([]);
  const [activeCase, setActiveCase] = useState(() => { try { return localStorage.getItem(ACTIVE_CASE_KEY) || ''; } catch { return ''; } });
  useEffect(() => { getHackIncidents().then((r) => setCases(r.incidents || [])).catch(() => {}); }, []);
  const casePicks = useMemo(() => {
    const vi = (i18n.language || 'vi').startsWith('vi');
    const name = (o) => (vi ? o?.vi || o?.en : o?.en || o?.vi) || '';
    return cases.flatMap((inc) => (inc.traces || []).map((tr) => ({
      key: `${inc.id}:${tr.id}`, trace: tr, incidentId: inc.id,
      label: `${name(inc.title)} — ${name(tr.title)} (${tr.from} → ${tr.to})`,
    })));
  }, [cases, i18n.language]);

  // Opens one of a case's traces: fills the form, runs it, and shows the
  // case panel under the graph. Remembered so a reload reopens the case.
  const openCaseTrace = useCallback((incidentId, tr) => {
    const key = `${incidentId}:${tr.id}`;
    setActiveCase(key);
    try { localStorage.setItem(ACTIVE_CASE_KEY, key); } catch { /* non-fatal */ }
    setSeedInput(tr.seeds.join('; '));
    setChain(tr.chain); setDepth(tr.depth); setFrom(tr.from); setTo(tr.to);
    runLoad(tr.seeds, tr.chain, tr.depth, tr.from, tr.to);
  }, []);
  // Earlier traces stored on the backend (any case, any user) — reopened
  // exactly as saved, without a new Nansen pull. Traces that are one of the
  // hack cases above are left out of this group.
  const [history, setHistory] = useState([]);
  const [activeSnap, setActiveSnap] = useState('');
  useEffect(() => { getFlowGraphHistory().then((r) => setHistory(r.traces || [])).catch(() => {}); }, []);
  const historyPicks = useMemo(() => {
    const day = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
    const sig = (seeds, chain, from, to) => `${[...seeds].map((x) => x.toLowerCase()).sort().join(',')}|${chain}|${day(from)}|${day(to)}`;
    const caseSigs = new Set(casePicks.map((c) => sig(c.trace.seeds, c.trace.chain, c.trace.from, c.trace.to)));
    const chainLabel = (c) => CHAINS.find((x) => x.value === c)?.label || c;
    return history
      .filter((h) => !caseSigs.has(sig(h.seeds, h.chain, h.from, h.to)))
      .map((h) => ({
        key: `snap:${h.id}`, id: h.id,
        label: `${day(h.fetchedAt)} · ${chainLabel(h.chain)} · ${h.seeds.length > 1 ? t('flowGraph.historyWallets', { count: h.seeds.length, first: shortAddr(h.seeds[0]) }) : shortAddr(h.seeds[0] || '?')} · ${day(h.from)} → ${day(h.to)} · ${t('flowGraph.historyNodes', { count: h.nodeCount })}`,
      }));
  }, [history, casePicks, t]);

  const openSnapshot = useCallback(async (key) => {
    const id = key.slice('snap:'.length);
    setActiveSnap(key);
    setActiveCase('');
    try { localStorage.removeItem(ACTIVE_CASE_KEY); } catch { /* non-fatal */ }
    setLoading(true); setError('');
    try {
      const snap = await getFlowGraphSnapshot(id);
      const day = (d) => new Date(d).toISOString().slice(0, 10);
      setSeedInput(snap.seeds.join('; '));
      setChain(snap.chain); setDepth(snap.depth); setFrom(day(snap.from)); setTo(day(snap.to));
      setFocusAddress(null); setActiveNode(null);
      setGraph(snap); setPositions({});
      try {
        localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify({
          seedInput: snap.seeds.join('; '), chain: snap.chain, depth: snap.depth, from: day(snap.from), to: day(snap.to), graph: snap, positions: {},
        }));
      } catch { /* non-fatal */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const activeIncidentId = activeCase ? activeCase.split(':')[0] : null;
  const activeTraceId = activeCase ? activeCase.split(':')[1] : null;
  useEffect(() => {
    const linkSeeds = searchParams.get('seeds');
    if (linkSeeds) {
      const p = {
        seeds: parseSeeds(linkSeeds),
        chain: CHAINS.some((c) => c.value === searchParams.get('chain')) ? searchParams.get('chain') : 'ethereum',
        depth: Math.min(Math.max(parseInt(searchParams.get('depth'), 10) || 2, 1), 3),
        from: searchParams.get('from') || monthsAgoIso(1),
        to: searchParams.get('to') || todayIso(),
      };
      setSeedInput(p.seeds.join('; '));
      setChain(p.chain); setDepth(p.depth); setFrom(p.from); setTo(p.to);
      runLoad(p.seeds, p.chain, p.depth, p.from, p.to);
      return;
    }
    try {
      const raw = localStorage.getItem(LAST_SEARCH_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || !saved.graph) return;
      setSeedInput(saved.seedInput || '');
      setChain(saved.chain || 'ethereum');
      setDepth(saved.depth || 2);
      setFrom(saved.from || monthsAgoIso(1));
      setTo(saved.to || todayIso());
      setGraph(saved.graph);
      setPositions(saved.positions || {});
    } catch { /* corrupt/old entry — ignore, page just starts empty */ }
  }, []);

  // Rebuilds the rendered nodes/edges whenever the loaded graph, the
  // double-click focus, OR manual drag positions change — single source of
  // truth instead of duplicating the build+set calls in both load() and the
  // restore effect.
  useEffect(() => {
    if (!graph) { setNodes([]); setEdges([]); setHopStats(null); return; }
    const { nodes: n, edges: e, visibleHopCounts, rawHopCounts } = buildFlowElements(graph, focusAddress, positions);
    setNodes(n);
    setEdges(e);
    setHopStats({ visibleHopCounts, rawHopCounts });
  }, [graph, focusAddress, positions, setNodes, setEdges]);

  // Persist manual drag positions alongside the current search so an F5
  // doesn't lose them (mirrors the graph itself, already saved this way).
  useEffect(() => {
    if (!graph) return;
    try {
      const raw = localStorage.getItem(LAST_SEARCH_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify({ ...saved, positions }));
    } catch { /* non-fatal — just won't persist this drag */ }
  }, [positions, graph]);

  const handleNodeDragStop = useCallback((_event, node) => {
    setPositions((prev) => ({ ...prev, [node.id]: { x: node.position.x, y: node.position.y } }));
  }, []);

  // Shared fetch core — takes explicit params rather than reading component
  // state, so both the form's "Truy vết" button AND opening a saved screen
  // (which sets state that hasn't necessarily re-rendered yet) can call it
  // with values that are guaranteed current. `initialPositions` lets a
  // reopened screen restore its own saved manual layout instead of starting
  // from the fresh auto-layout every new search gets.
  const runLoad = useCallback(async (seedsArr, chainVal, depthVal, fromVal, toVal, forceRefresh = false, initialPositions = {}) => {
    if (!seedsArr || seedsArr.length === 0) { setError(t('flowGraph.errNoWalletInput')); return; }
    setLoading(true);
    setError('');
    try {
      const result = await getFlowGraph({ seeds: seedsArr, chain: chainVal, depth: depthVal, from: fromVal, to: toVal, forceRefresh });
      setFocusAddress(null);
      setActiveNode(null);
      setGraph(result);
      setPositions(initialPositions);
      // A new search always replaces whatever was saved before — only the
      // most recent lookup is ever kept client-side.
      try {
        localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify({
          seedInput: seedsArr.join('; '), chain: chainVal, depth: depthVal, from: fromVal, to: toVal,
          graph: result, positions: initialPositions,
        }));
      } catch { /* quota exceeded / private mode — non-fatal, just don't persist */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const load = useCallback((forceRefresh = false) => {
    // A hand-made search is no longer the picked hack case (unless it's the
    // same case re-run, e.g. force refresh) — close the case panel then.
    const pick = casePicks.find((c) => c.key === activeCase);
    const same = pick && pick.trace.seeds.join('; ') === parseSeeds(seedInput).join('; ') && pick.trace.chain === chain;
    if (activeCase && !same) {
      setActiveCase('');
      try { localStorage.removeItem(ACTIVE_CASE_KEY); } catch { /* non-fatal */ }
    }
    setActiveSnap('');
    runLoad(parseSeeds(seedInput), chain, depth, from, to, forceRefresh);
  }, [seedInput, chain, depth, from, to, runLoad, casePicks, activeCase]);

  const handleNodeDoubleClick = useCallback((_event, node) => {
    const isSeed = graph?.nodes.some((n) => n.hop === 0 && n.address === node.id);
    setFocusAddress(isSeed ? null : node.id);
  }, [graph]);

  // Single click opens/switches the pinned detail panel — reads the node's
  // own `raw` field (attached in buildFlowElements) rather than re-searching
  // graph.nodes, since a meta-node's id never appears there.
  const handleNodeClick = useCallback((_event, node) => {
    if (node.data?.raw) setActiveNode(node.data.raw);
  }, []);

  const stats = useMemo(() => {
    if (!graph) return null;
    return {
      nodeCount: nodes.length, edgeCount: edges.length,
      rawNodeCount: graph.nodeCount, rawEdgeCount: graph.edgeCount,
      creditsCost: graph.creditsCost, cached: graph.cached, truncated: graph.truncated,
      fetchedAt: graph.fetchedAt,
    };
  }, [graph, nodes, edges]);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h2 style={s.title}>{t('flowGraph.title')}</h2>
        <p style={s.subtitle}>{t('flowGraph.subtitle')}</p>
      </div>

      {(casePicks.length > 0 || historyPicks.length > 0) && (
        <div style={s.caseBar}>
          <span style={s.fieldLabel}>📂 {t('flowGraph.casePick')}</span>
          <select
            style={{ ...s.input, minWidth: 280, maxWidth: '100%' }}
            value={activeCase || activeSnap}
            onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith('snap:')) { openSnapshot(v); return; }
              setActiveSnap('');
              const pick = casePicks.find((c) => c.key === v);
              if (pick) openCaseTrace(pick.incidentId, pick.trace); else setActiveCase('');
            }}
          >
            <option value="">{t('flowGraph.casePickNone')}</option>
            {casePicks.length > 0 && (
              <optgroup label={`🚨 ${t('flowGraph.groupCases')}`}>
                {casePicks.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </optgroup>
            )}
            {historyPicks.length > 0 && (
              <optgroup label={`🕘 ${t('flowGraph.groupHistory')}`}>
                {historyPicks.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </optgroup>
            )}
          </select>
          {activeIncidentId && (
            <button
              type="button" style={s.aiJump}
              onClick={() => document.getElementById('hack-lifetime')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              🤖 {t('flowGraph.jumpLifetime')}
            </button>
          )}
        </div>
      )}

      <div style={s.form}>
        <div style={{ ...s.field, flex: 2, minWidth: 260 }}>
          <span style={s.fieldLabel}>{t('flowGraph.fieldAddresses')}</span>
          <input
            style={s.input}
            value={seedInput}
            onChange={(e) => setSeedInput(e.target.value)}
            placeholder={t('flowGraph.addressPlaceholder')}
          />
        </div>
        <div style={s.field}>
          <span style={s.fieldLabel}>{t('flowGraph.fieldNetwork')}</span>
          <select style={s.input} value={chain} onChange={(e) => setChain(e.target.value)}>
            {CHAINS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div style={{ ...s.field, width: 80 }}>
          <span style={s.fieldLabel}>{t('flowGraph.fieldDepth')}</span>
          <select style={s.input} value={depth} onChange={(e) => setDepth(Number(e.target.value))}>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        </div>
        <div style={s.field}>
          <span style={s.fieldLabel}>{t('flowGraph.fieldFromDate')}</span>
          <input style={s.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div style={s.field}>
          <span style={s.fieldLabel}>{t('flowGraph.fieldToDate')}</span>
          <input style={s.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button style={s.btnPrimary} onClick={() => load(false)} disabled={loading}>
          {loading ? t('flowGraph.btnTracing') : t('flowGraph.btnTrace')}
        </button>
        <button style={s.btnGhost} onClick={() => load(true)} disabled={loading} title={t('flowGraph.forceRefreshTitle')}>
          {t('flowGraph.btnForceRefresh')}
        </button>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      {stats && (
        <div style={s.statsRow}>
          <span style={s.statChip}>{t('flowGraph.statNodesShown', { shown: stats.nodeCount, total: stats.rawNodeCount })}</span>
          <span style={s.statChip}>{t('flowGraph.statFlowsShown', { shown: stats.edgeCount, min: fmtUsd(MIN_EDGE_USD), total: stats.rawEdgeCount })}</span>
          <span style={{ ...s.statChip, color: stats.cached ? '#10b981' : '#f59e0b' }}>
            {stats.cached ? t('flowGraph.statFromCache') : t('flowGraph.statFresh', { count: stats.creditsCost })}
          </span>
          {stats.truncated && <span style={{ ...s.statChip, color: '#dc2626' }}>{t('flowGraph.statTruncated')}</span>}
          <span style={s.legendChip}><span style={{ ...s.legendDot, background: '#34d399' }} /> {t('flowGraph.legendOutflow')}</span>
          <span style={s.legendChip}><span style={{ ...s.legendDot, background: '#38bdf8' }} /> {t('flowGraph.legendInflow')}</span>
        </div>
      )}

      {hopStats && (
        <div style={s.statsRow}>
          <span style={s.statChip} title={t('flowGraph.byHopTitle')}>
            {t('flowGraph.byHopLabel', {
              summary: Object.keys({ ...hopStats.rawHopCounts, ...hopStats.visibleHopCounts })
                .map(Number).sort((a, b) => a - b)
                .map((hop) => `Hop ${hop}: ${hopStats.visibleHopCounts[hop] || 0}/${hopStats.rawHopCounts[hop] || 0}`)
                .join('  •  '),
            })}
          </span>
          {focusAddress && (
            <button style={s.btnGhost} onClick={() => setFocusAddress(null)} title={focusAddress}>
              {t('flowGraph.focusActive')}
            </button>
          )}
        </div>
      )}

      <div style={s.canvasWrap} className="tf-dark-flow">
        {/* Scoped overrides for ReactFlow's own light-themed chrome
            (Controls buttons, attribution) — keeps the dark canvas look
            consistent without a global theme change. */}
        <style>{`
          .tf-dark-flow .react-flow__controls-button {
            background: #131a2b; border-bottom: 1px solid #232b45; fill: #c3c9dc;
          }
          .tf-dark-flow .react-flow__controls-button:hover { background: #1b2440; }
          .tf-dark-flow .react-flow__controls { border: 1px solid #232b45; border-radius: 8px; overflow: hidden; }
          .tf-dark-flow .react-flow__attribution { background: transparent; }
        `}</style>
        {nodes.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>🕸️</div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              {t('flowGraph.emptyPrompt')}
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            onNodeDragStop={handleNodeDragStop}
            fitView proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#1e2740" />
            <Controls />
            <MiniMap
              pannable zoomable
              nodeColor={(n) => n.data?.minimapColor || '#4b5563'}
              nodeStrokeColor={(n) => n.data?.minimapColor || '#4b5563'}
              nodeStrokeWidth={2}
              nodeBorderRadius={8}
              maskColor="rgba(0,0,0,0.55)"
              style={{ background: '#0b0f1a', border: '1px solid #232b45' }}
            />
            {/* Panel renders outside the pan/zoom-transformed layer, so this
                stays fixed-size and pinned to the corner at any zoom level. */}
            <Panel position="top-left">
              <PinnedTooltip node={activeNode} onClose={() => setActiveNode(null)} />
            </Panel>
            <Panel position="bottom-left">
              <GraphLegend />
            </Panel>
          </ReactFlow>
        )}
      </div>

      {activeIncidentId && cases.some((c) => c.id === activeIncidentId) && (
        <HackCasePanel
          incidentId={activeIncidentId}
          activeTraceId={activeTraceId}
          onPickTrace={(tr) => openCaseTrace(activeIncidentId, tr)}
        />
      )}
    </div>
  );
}

const s = {
  // No fixed viewport-height cap here — the canvas below gets a generous
  // explicit height instead, and the page just scrolls normally when the
  // graph is taller than the visible area.
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto', display: 'flex', flexDirection: 'column' },
  header: { marginBottom: 16 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  aiJump: { fontSize: 12.5, fontWeight: 700, color: '#fff', background: 'var(--accent)', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' },
  caseBar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  form: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', fontSize: 13, color: 'var(--text)' },
  btnPrimary: { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },

  errorBox: { background: '#dc262614', border: '1px solid #dc262655', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 },

  statsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  statChip: { fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px' },
  legendChip: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-dim)', padding: '4px 6px' },
  legendDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },

  // Always dark, independent of the app's light/dark theme toggle — the
  // graph reads as an investigative "console" and stays legible either way.
  // `flex: 1` (== flex-basis: 0%) would silently override the explicit
  // height below, and since `page` no longer has a fixed height for it to
  // grow into, ReactFlow's own size measurement on mount could resolve to
  // 0 — canvas renders as an empty black box with nothing on it. A plain
  // fixed height with no flex-grow avoids that ambiguity entirely.
  canvasWrap: { height: 750, background: '#05070d', border: '1px solid #232b45', borderRadius: 14, overflow: 'hidden', position: 'relative' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' },
};
